<template>
    <div v-if="attributes && attributes.length > 0">
        <div v-for="(parent, idx) in attributes" :key="idx" class="mt-6">
            <div 
                v-if="parent?.show_in_filter"
                @click="() => toggleAttribute(parent?.id)"
                class="flex items-center justify-between transition-height-transform duration-200 delay-300 cursor-pointer hover:bg-gray-50 rounded-lg group">
                <div class="flex flex-col gap-2 flex-1">
                    <span class="font-semibold text-lg group-hover:text-primary transition-colors duration-200">{{ $i18n.locale === 'ar' ? parent?.name_ar : parent?.name_en }}</span>
                    <!-- Display selected attributes -->
                    <div v-if="getSelectedAttributesForParent(parent.id).length > 0" class="flex flex-wrap gap-2 mt-2">
                        <div 
                            v-for="selectedAttr in getSelectedAttributesForParent(parent.id)" 
                            :key="selectedAttr.id"
                            class="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 text-sm hover:bg-primary/15 transition-colors duration-200 group"
                        >
                            <!-- Color indicator if available -->
                            <span 
                                v-if="selectedAttr.color" 
                                class="w-3 h-3 rounded-full border border-gray-300 shadow-sm flex-shrink-0"
                                :style="{ backgroundColor: selectedAttr.color }"
                            ></span>
                            
                            <!-- Attribute name -->
                            <span class="text-primary font-medium text-xs">
                                {{ selectedAttr.name }}
                            </span>
                            
                            <!-- Parent name for nested attributes -->
                            <span 
                                v-if="selectedAttr.type === 'nested' && selectedAttr.parentName" 
                                class="text-gray-500 text-xs opacity-75"
                            >
                                ({{ selectedAttr.parentName }})
                            </span>
                            
                            <!-- Remove button -->
                            <button 
                                @click.stop="removeAttribute(parent.id, selectedAttr.id, selectedAttr.type)"
                                class="ml-1 text-gray-400 hover:text-red-500 transition-colors duration-200 p-0.5 rounded-full hover:bg-red-50 group-hover:bg-red-50"
                                :title="$t('general.remove')"
                            >
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <span class="w-8 h-8 flex items-center justify-center">
                    <img :class="` ${!parent?.isOpen ? ' rotate-90 ' : ' rotate-0 '} transition-height-transform `"
                        src="../../assets/icons/down.svg" alt="">
                </span>
            </div>
            <div
                :class="`${!parent?.isOpen ? 'min-h-[0px]' : 'min-h-[20px]'} transition-height-transform text-[14px] text-[#484848BD]`">
                <div
                    :class="`${parent?.isOpen ? 'flex' : 'hidden'} flex flex-col gap-1 text-[14px] mt-4`">
                    
                    <!-- Add search input (hidden for year attributes) -->
                    <div v-if="!parent.is_year" class="mb-4">
                        <div class="relative">
                            <input 
                                v-model="searchInputs[parent.id]"
                                @input="() => filterChildren(parent.id)"
                                class="w-full px-4 py-3 pr-10 text-sm border border-[#C6C6C6] rounded-md focus:outline-none focus:border-primary"
                                :placeholder="$t('search.search_attributes')"
                                type="text"
                            >
                            <span class="absolute right-3 top-1/2 -translate-y-1/2">
                                <img src="../../assets/icons/search.svg" alt="" class="w-4 h-4 opacity-50">
                            </span>
                        </div>
                    </div>

                    <div v-if="parent?.children?.length" class="space-y-2 overflow-hidden">
                        <!-- Year Range Slider for year attributes -->
                        <YearRangeSlider
                            v-if="parent.is_year && yearRangeLimits[parent.id]"
                            :min-limit="yearRangeLimits[parent.id].min"
                            :max-limit="yearRangeLimits[parent.id].max"
                            :selected-year-range="yearRangeValues[parent.id]"
                            :year-label="$i18n.locale === 'ar' ? parent?.name_ar : parent?.name_en"
                            @year-change="(range) => handleYearChange(parent.id, range)"
                        ></YearRangeSlider>
                        
                        <!-- Existing checkbox list for non-year attributes -->
                        <div v-else>
                        <div v-if="getFilteredChildren(parent).length > 0">
                            <div v-for="(child, i) in getFilteredChildren(parent)" :key="i" class="mb-2">
                                <!-- Main child attribute -->
                                <div 
                                    @click="(e) => child.attributes && child.attributes.length > 0 ? handleNestedClick(e, parent.id, child) : handleAttributeCheck(parent.id, child.id)"
                                    class="cursor-pointer flex items-center gap-3 mb-4 hover:bg-gray-50 p-4 rounded-lg transition-colors duration-200 group border border-gray-100 hover:border-primary hover:shadow-sm"
                                >
                                    <input 
                                        :checked="child.isChecked" 
                                        @click.stop="(e) => child.attributes && child.attributes.length > 0 ? handleNestedClick(e, parent.id, child) : handleAttributeCheck(parent.id, child.id)"
                                        type="checkbox" 
                                        class="w-5 h-5 border-gray-300 rounded cursor-pointer focus:ring-primary focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                                        name="" 
                                        id="">
                                    <div class="flex items-center gap-3 flex-1">
                                        <span v-if="child.color" 
                                            class="block w-[35px] h-[35px] rounded-full border-2 border-[#E5E5E5] shadow-sm transition-all duration-200 
                                                   group-hover:shadow-md group-hover:scale-105 group-hover:border-primary"
                                            :style="{ backgroundColor: child.color }">
                                        </span>
                                        <span class="text-[15px] text-gray-800 transition-all duration-200 
                                                 group-hover:text-primary group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 font-semibold">
                                            {{ $i18n.locale === 'ar' ? child?.name_ar : child?.name_en }}
                                        </span>
                                        <!-- Arrow indicator for nested attributes -->
                                        <span 
                                            v-if="child.attributes && child.attributes.length > 0"
                                            class="flex items-center justify-center ml-auto text-gray-400 group-hover:text-primary transition-colors duration-200"
                                            :title="$t('search.open_submenu') || 'Open submenu'"
                                        >
                                            <svg 
                                                class="w-4 h-4 ltr:rotate-0 rtl:rotate-180" 
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path 
                                                    stroke-linecap="round" 
                                                    stroke-linejoin="round" 
                                                    stroke-width="2" 
                                                    d="M9 5l7 7-7 7"
                                                ></path>
                                            </svg>
                                        </span>
                                    </div>
                                </div>

                                <!-- Nested attributes are now shown in overlay instead of inline -->
                            </div>
                        </div>
                        <div v-else class="text-gray-500 text-sm py-2">
                            {{ $t('search.no_results') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { YearRangeSlider } from "@/components"
import { filterNestedAttributes } from '@/utils/filterUtils'

export default {
    name: 'FilterAttributes',
    components: {
        YearRangeSlider
    },
    props: {
        attributes: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedAttributeIds: {
            type: Array,
            default: () => []
        },
        yearRangeLimits: {
            type: Object,
            default: () => ({})
        },
        yearRangeValues: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['attribute-toggled', 'attribute-checked', 'nested-attribute-checked', 'year-range-changed', 'nested-overlay-requested', 'attribute-removed'],
    data() {
        return {
            searchInputs: {},
            filteredChildren: {}
        }
    },
    computed: {
        getSelectedAttributesForParent() {
            return (parentId) => {
                const parent = this.attributes.find(attr => attr.id === parentId)
                if (!parent || !parent.children || !Array.isArray(parent.children)) return []
                
                // Handle year attributes - show selected year range
                if (parent.is_year && this.yearRangeValues[parentId] !== undefined && this.yearRangeValues[parentId] !== null) {
                    const selectedRange = this.yearRangeValues[parentId]
                    if (selectedRange && typeof selectedRange === 'object' && selectedRange.min !== undefined && selectedRange.max !== undefined) {
                        // Only show if range is not the full range (user has made a selection)
                        const limits = this.yearRangeLimits[parentId]
                        if (limits && (selectedRange.min !== limits.min || selectedRange.max !== limits.max)) {
                            return [{
                                id: `year_${parentId}_${selectedRange.min}_${selectedRange.max}`,
                                name: `${this.$t('search.from') || 'From'} ${selectedRange.min} ${this.$t('general.to') || 'To'} ${selectedRange.max}`,
                                type: 'year',
                                yearRange: selectedRange
                            }]
                        }
                    }
                    return []
                }
                
                const selectedAttributes = []
                
                parent.children.forEach(child => {
                    if (!child) return
                    
                    // Check if this child has any nested attributes that are selected
                    let hasSelectedNestedAttributes = false
                    
                    if (child.attributes && Array.isArray(child.attributes)) {
                        child.attributes.forEach(nestedAttr => {
                            if (!nestedAttr) return
                            
                            if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                                nestedAttr.children.forEach(nestedChild => {
                                    if (!nestedChild) return
                                    
                                    if (nestedChild.isChecked) {
                                        hasSelectedNestedAttributes = true
                                        
                                        const nestedName = this.$i18n.locale === 'ar' 
                                            ? (nestedChild.name_ar || nestedChild.name_en || '') 
                                            : (nestedChild.name_en || nestedChild.name_ar || '')
                                        
                                        const parentName = this.$i18n.locale === 'ar' 
                                            ? (child.name_ar || child.name_en || '') 
                                            : (child.name_en || child.name_ar || '')
                                        
                                        if (nestedName) {
                                            selectedAttributes.push({
                                                id: nestedChild.id,
                                                name: nestedName,
                                                color: nestedChild.color,
                                                type: 'nested',
                                                parentName: parentName
                                            })
                                        }
                                    }
                                })
                            }
                        })
                    }
                    
                    // Only add the child attribute if it's checked AND no nested attributes are selected
                    if (child.isChecked && !hasSelectedNestedAttributes) {
                        const name = this.$i18n.locale === 'ar' 
                            ? (child.name_ar || child.name_en || '') 
                            : (child.name_en || child.name_ar || '')
                        
                        if (name) {
                            selectedAttributes.push({
                                id: child.id,
                                name: name,
                                color: child.color,
                                type: 'child'
                            })
                        }
                    }
                })
                
                return selectedAttributes
            }
        }
    },
    methods: {
        toggleAttribute(attributeId) {
            this.$emit('attribute-toggled', attributeId)
        },
        handleAttributeCheck(parentId, childId) {
            this.$emit('attribute-checked', { parentId, childId })
        },
        handleNestedClick(event, parentId, child) {
            this.$emit('nested-overlay-requested', { event, parentId, child })
        },
        handleYearChange(parentId, range) {
            this.$emit('year-range-changed', { parentId, range })
        },
        removeAttribute(parentId, attrId, type) {
            this.$emit('attribute-removed', { parentId, attrId, type })
        },
        getFilteredChildren(parent) {
            if (!this.searchInputs[parent.id]) {
                return parent.children
            }
            return this.filteredChildren[parent.id] || parent.children
        },
        filterChildren(parentId) {
            const searchTerm = this.searchInputs[parentId]?.toLowerCase() || ''
            const parent = this.attributes.find(attr => attr.id === parentId)
            
            if (!parent?.children) return
            
            this.filteredChildren[parentId] = filterNestedAttributes(parent, searchTerm)
        }
    }
}
</script>

<style scoped>
.transition-height-transform {
    transition-property: min-height, transform;
    transition-duration: 300ms;
    transition-timing-function: ease-in-out;
}
</style>

